import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BellRing,
  CheckCircle2,
  Copy,
  Menu,
  Network,
  ShieldCheck,
  Sparkles,
  Target,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { FAQSection } from '@/components/FAQSection';
import { ProductMockup } from '@/components/ProductMockup';
import { TrustedBy } from '@/components/TrustedBy';

const features = [
  {
    icon: Target,
    title: 'Your voice, organized',
    description: 'Turn a LinkedIn profile into a clear writing workspace with voice, goals and angles in one place.',
  },
  {
    icon: Network,
    title: 'Know what to say',
    description: 'Spot useful signals from the people that matter, without scrolling LinkedIn all day.',
  },
  {
    icon: BellRing,
    title: 'Stay timely',
    description: 'Catch relevant moments early and turn them into posts, comments or replies.',
  },
  {
    icon: Sparkles,
    title: 'Draft faster',
    description: 'Prepare sharp LinkedIn posts and comments in English, Italian or Polish.',
  },
  {
    icon: Copy,
    title: 'Keep it human',
    description: 'BeLoud prepares the draft. You keep the final word.',
  },
  {
    icon: ShieldCheck,
    title: 'Simple control',
    description: 'Clear usage limits, simple pricing and no bloated enterprise setup.',
  },
];

const workflow = [
  'Connect a LinkedIn profile.',
  'Define the voice and positioning.',
  'Track useful signals from your network.',
  'Prepare posts and comments worth publishing.',
];

const pricingTiers = [
  {
    id: 'trial',
    name: 'Trial',
    price: '3 days',
    description: 'Try the writing workflow before paying.',
    features: ['Profile setup', 'Starter credits', 'Writing studio', 'EN / IT / PL output'],
    cta: 'Start trial',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '€19',
    suffix: '/month',
    description: 'For consultants, founders and operators building a recognizable LinkedIn presence.',
    features: ['Persona workspace', 'Network signals', 'Writing studio', 'Post and comment drafts', 'Simple usage limits'],
    cta: 'Choose Pro',
    highlighted: true,
  },
];

export function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const appUrl = import.meta.env.VITE_FRONTEND_URL || window.location.origin;

  const goApp = (path: '/login' | '/signup') => {
    window.location.href = `${appUrl}${path}`;
  };

  const goSignup = () => goApp('/signup');

  return (
    <div className="min-h-screen bg-background text-foreground">
      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.45 }}
        className="sticky top-0 z-50 border-b-2 border-border bg-background/95 backdrop-blur"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <button className="flex items-center gap-3" onClick={() => navigate('/')} aria-label="beloud.pro home">
            <div className="flex h-9 w-9 items-center justify-center border-2 border-foreground bg-primary text-primary-foreground shadow-[4px_4px_0px_hsl(var(--foreground))]">
              <span className="font-black">B</span>
            </div>
            <span className="text-xl font-black tracking-tight">beloud.pro</span>
          </button>

          <div className="hidden items-center gap-8 md:flex">
            <a href="#features" className="text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">Features</a>
            <a href="#workflow" className="text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">Workflow</a>
            <a href="#pricing" className="text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">Pricing</a>
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <Button variant="ghost" onClick={() => goApp('/login')}>Sign in</Button>
            <Button onClick={goSignup}>Start trial</Button>
          </div>

          <button className="md:hidden" onClick={() => setMobileMenuOpen((value) => !value)} aria-label="Open menu">
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {mobileMenuOpen ? (
          <div className="border-t-2 border-border px-4 py-4 md:hidden">
            <div className="flex flex-col gap-4 text-sm font-bold uppercase tracking-[0.18em]">
              <a href="#features" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#workflow" onClick={() => setMobileMenuOpen(false)}>Workflow</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
              <Button onClick={goSignup}>Start trial</Button>
            </div>
          </div>
        ) : null}
      </motion.nav>

      <section className="relative overflow-hidden border-b-2 border-border px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="absolute inset-0 brutalist-grid opacity-60" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
            <div className="mb-6 inline-flex border-2 border-border bg-accent px-4 py-2 text-xs font-black uppercase tracking-[0.24em] text-accent-foreground shadow-[5px_5px_0px_hsl(var(--foreground))]">
              LinkedIn growth workspace
            </div>
            <h1 className="max-w-4xl text-5xl font-black leading-[0.95] tracking-[-0.06em] sm:text-6xl lg:text-7xl">
              Stop posting louder. Start being worth noticing.
            </h1>
            <p className="mt-6 max-w-2xl text-lg font-medium text-muted-foreground sm:text-xl">
              BeLoud.pro helps founders, consultants and agencies find sharper angles, prepare better LinkedIn posts and show up with a voice people recognize.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" onClick={goSignup} className="group">
                Start 3-day trial
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById('workflow')?.scrollIntoView({ behavior: 'smooth' })}>
                See workflow
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 text-sm font-semibold text-muted-foreground">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> EN / IT / PL</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Voice-first drafts</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> €19/month</span>
            </div>
          </motion.div>

          <ProductMockup />
        </div>
      </section>

      <TrustedBy />

      <section id="features" className="border-b-2 border-border px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="brutalist-label mb-3 inline-flex">What it does</p>
            <h2 className="text-4xl font-black tracking-[-0.04em] sm:text-5xl">Built for people who need better ideas, not more noise.</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card key={feature.title} className="brutalist-panel h-full">
                  <CardHeader>
                    <div className="mb-4 flex h-12 w-12 items-center justify-center border-2 border-border bg-primary text-primary-foreground">
                      <Icon className="h-6 w-6" />
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-6 text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section id="workflow" className="border-b-2 border-border bg-muted/40 px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="brutalist-label mb-3 inline-flex">Workflow</p>
            <h2 className="text-4xl font-black tracking-[-0.04em]">From profile to publishable draft.</h2>
            <p className="mt-4 text-muted-foreground">A focused workflow for turning voice, context and timing into stronger LinkedIn writing.</p>
          </div>
          <div className="grid gap-4">
            {workflow.map((step, index) => (
              <div key={step} className="brutalist-panel flex gap-4 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-border bg-accent font-black text-accent-foreground">
                  {index + 1}
                </div>
                <p className="pt-2 font-semibold">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="border-b-2 border-border px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <p className="brutalist-label mb-3 inline-flex">Pricing</p>
            <h2 className="text-4xl font-black tracking-[-0.04em]">Simple enough to launch.</h2>
            <p className="mt-4 text-muted-foreground">Start small. Keep the workflow simple. Upgrade when LinkedIn becomes a real channel.</p>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {pricingTiers.map((tier) => (
              <Card key={tier.id} className={`brutalist-panel h-full ${tier.highlighted ? 'bg-primary text-primary-foreground' : ''}`}>
                <CardHeader>
                  <CardTitle className="text-2xl">{tier.name}</CardTitle>
                  <p className={tier.highlighted ? 'text-primary-foreground/75' : 'text-muted-foreground'}>{tier.description}</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-end gap-2">
                    <span className="text-5xl font-black tracking-[-0.05em]">{tier.price}</span>
                    {tier.suffix ? <span className={tier.highlighted ? 'text-primary-foreground/75' : 'text-muted-foreground'}>{tier.suffix}</span> : null}
                  </div>
                  <ul className="space-y-2 text-sm font-medium">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle2 className={`mt-0.5 h-4 w-4 ${tier.highlighted ? 'text-accent' : 'text-primary'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full" variant={tier.highlighted ? 'secondary' : 'default'} onClick={goSignup}>{tier.cta}</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <FAQSection />

      <section className="border-y-2 border-border bg-primary px-4 py-16 text-primary-foreground sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div>
            <p className="mb-2 text-sm font-black uppercase tracking-[0.24em] text-primary-foreground/70">Ready?</p>
            <h2 className="max-w-3xl text-4xl font-black tracking-[-0.04em]">Build a LinkedIn presence with signal, not spam.</h2>
          </div>
          <Button size="lg" variant="secondary" onClick={goSignup}>Start trial</Button>
        </div>
      </section>

      <footer className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-6 text-sm text-muted-foreground md:flex-row md:items-center">
          <div>
            <p className="text-lg font-black text-foreground">beloud.pro</p>
            <p>LinkedIn growth workspace for managed personas.</p>
          </div>
          <div className="flex gap-5">
            <a href="mailto:admin@beloud.pro" className="hover:text-foreground">Contact</a>
            <a href="/privacy" className="hover:text-foreground">Privacy</a>
            <a href="/terms" className="hover:text-foreground">Terms</a>
            <span>© 2026 beloud.pro</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
